import { type ReactElement, useState, useEffect, useCallback, useRef } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useOtpVerification } from '@/hooks/use-otp-verification';
import { resourceRoutes } from '@/route-paths';
import type { AuthorizePasswordlessEmailResponse } from '@/routes/action.authorize-passwordless-email';
import AuthStepShell from '../auth-step-shell';
import { useLoginFlowVariant } from '../variant';

type VerifyOtpResponse = {
    success: boolean;
    error?: { code: string; message: string };
};

interface VerifyCodeStepProps {
    email: string;
    otpLength: number;
    onSuccess: () => void;
    onResend: () => void;
    onBackToAuthMethods: () => void;
}

export default function VerifyCodeStep({
    email,
    otpLength,
    onSuccess,
    onResend,
    onBackToAuthMethods,
}: VerifyCodeStepProps): ReactElement {
    const { t } = useTranslation('login');
    const isPage = useLoginFlowVariant() === 'page';
    const verifyFetcher = useFetcher<VerifyOtpResponse>();
    const resendFetcher = useFetcher<AuthorizePasswordlessEmailResponse>();
    const [error, setError] = useState<string | null>(null);
    const [showCodeResent, setShowCodeResent] = useState(false);
    const hasCalledOnSuccessRef = useRef(false);

    const isVerifying = verifyFetcher.state === 'submitting' || verifyFetcher.state === 'loading';

    const { otpInputs, otpInputsRef, refCallbacks } = useOtpVerification({
        slotCount: otpLength,
    });

    const isComplete = otpInputs.values.every((v) => v !== '');

    const handleSubmitVerify = useCallback(() => {
        const code = otpInputs.values.join('');
        if (code.length !== otpLength) return;

        setError(null);
        const formData = new FormData();
        formData.append('otpCode', code);
        formData.append('email', email);
        void verifyFetcher.submit(formData, {
            method: 'POST',
            action: resourceRoutes.verifyPasswordlessOtp,
        });
    }, [otpInputs.values, otpLength, email, verifyFetcher]);

    useEffect(() => {
        if (verifyFetcher.state !== 'idle' || !verifyFetcher.data) return;

        if (verifyFetcher.data.success && !hasCalledOnSuccessRef.current) {
            hasCalledOnSuccessRef.current = true;
            onSuccess();
        } else if (!verifyFetcher.data.success && verifyFetcher.data.error) {
            setError(t('sheet.otpVerifyError', { defaultValue: 'The code is incorrect, please re-enter' }));
        }
    }, [verifyFetcher.state, verifyFetcher.data, onSuccess, otpInputsRef, t]);

    useEffect(() => {
        if (verifyFetcher.state === 'submitting') {
            hasCalledOnSuccessRef.current = false;
        }
    }, [verifyFetcher.state]);

    const handleResend = useCallback(() => {
        if (showCodeResent) return;

        setError(null);
        setShowCodeResent(true);
        onResend();

        const formData = new FormData();
        formData.append('email', email);
        void resendFetcher.submit(formData, {
            method: 'POST',
            action: resourceRoutes.authorizePasswordlessEmail,
        });

        setTimeout(() => setShowCodeResent(false), 3000);
    }, [showCodeResent, email, resendFetcher, onResend]);

    const handleInputChange = (index: number, value: string) => {
        if (error) {
            otpInputsRef.current.clear();
            setError(null);
            if (value) {
                otpInputs.setValue(0, value);
            }
            return;
        }
        otpInputs.setValue(index, value);
    };

    const isResendDisabled = showCodeResent || isVerifying;

    return (
        <AuthStepShell
            heading={t('sheet.enterVerificationCode', { defaultValue: 'Enter Verification Code' })}
            onBack={onBackToAuthMethods}
            step={3}
            description={
                <>
                    {t('sheet.weSentCodeTo', { defaultValue: 'We sent a code to:' })}{' '}
                    <span className="font-medium text-foreground">{email}</span>
                </>
            }>
            <label className="mb-3 block text-sm font-medium text-foreground">
                {t('sheet.enterDigitCode', { otpLength, defaultValue: 'Enter {{otpLength}}-digit code' })}
            </label>

            <div
                className={cn('grid w-full gap-2', isPage && 'gap-1.5 sm:gap-2')}
                style={{ gridTemplateColumns: `repeat(${otpLength}, minmax(0, 1fr))` }}>
                {Array.from({ length: otpLength }, (_, index) => (
                    <Input
                        key={`otp-sheet-${index}`}
                        ref={refCallbacks[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={otpInputs.values[index] || ''}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        onKeyDown={(e) => otpInputs.handleKeyDown(index, e)}
                        onPaste={otpInputs.handlePaste}
                        disabled={isVerifying}
                        autoFocus={index === 0}
                        className={cn(
                            'h-12 min-w-0 px-0 text-center text-lg font-bold border-2',
                            isPage && 'h-13 sm:h-14',
                            error && '!border-status-critical !text-status-critical'
                        )}
                        aria-invalid={!!error}
                        aria-label={t('sheet.codeLabel', {
                            index: index + 1,
                            total: otpLength,
                            defaultValue: 'Verification code digit {{index}} of {{total}}',
                        })}
                    />
                ))}
            </div>

            {error ? <p className="mt-3 text-sm text-status-critical">{error}</p> : null}

            <Button
                type="button"
                className={cn('mt-6 w-full', isPage && 'h-12 text-base sm:h-14')}
                disabled={!isComplete || isVerifying}
                onClick={handleSubmitVerify}>
                {isVerifying
                    ? t('sheet.verifying', { defaultValue: 'Verifying...' })
                    : t('sheet.verify', { defaultValue: 'Verify' })}
            </Button>

            <div className="mt-6 text-center text-sm">
                {showCodeResent ? (
                    <span className="font-medium text-foreground">
                        {t('sheet.codeResent', { defaultValue: 'Code Resent' })}
                    </span>
                ) : (
                    <>
                        <span className="text-muted-foreground">
                            {t('sheet.didntReceiveCode', { defaultValue: "Didn't receive the code?" })}{' '}
                        </span>
                        <button
                            type="button"
                            className="cursor-pointer font-medium text-foreground underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={handleResend}
                            disabled={isResendDisabled}>
                            {t('sheet.resend', { defaultValue: 'Resend' })}
                        </button>
                    </>
                )}
            </div>

            <div className="mt-4 text-center">
                <button
                    type="button"
                    className="cursor-pointer text-sm text-foreground underline underline-offset-2"
                    onClick={onBackToAuthMethods}>
                    {t('sheet.tryAnotherWay', { defaultValue: 'Try another way to sign in' })}
                </button>
            </div>
        </AuthStepShell>
    );
}
